import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Play, Plus } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { Button } from '../../../components/Shared/Button';
import { Card } from '../../../components/Shared/Card';
import { Badge } from '../../../components/Shared/Badge';
import { EmptyState } from '../../../components/Shared/EmptyState';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/Shared/Table';
import { useTenantInvoiceStore } from '../../../store/useTenantInvoiceStore';
import { useAdminTenantStore } from '../../../store/useAdminTenantStore';
import { TenantInvoiceStatus } from '../../../api/types/admin';
import { formatDate } from '../../../utils/dateUtils';
import { formatMoney } from '../../../utils/money';
import { GenerateMonthlyModal } from './components/GenerateMonthlyModal';

function statusVariant(s: number | undefined): 'default' | 'info' | 'success' | 'danger' {
  switch (s) {
    case TenantInvoiceStatus.Issued:
      return 'info';
    case TenantInvoiceStatus.Paid:
      return 'success';
    case TenantInvoiceStatus.Cancelled:
      return 'danger';
    case TenantInvoiceStatus.Draft:
    default:
      return 'default';
  }
}

function statusLabelKey(s: number | undefined): string {
  switch (s) {
    case TenantInvoiceStatus.Issued:
      return 'issued';
    case TenantInvoiceStatus.Paid:
      return 'paid';
    case TenantInvoiceStatus.Cancelled:
      return 'cancelled';
    default:
      return 'draft';
  }
}

export default function TenantInvoicesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation(['admin', 'common']);

  const {
    invoices,
    filters,
    loading,
    saving,
    error,
    setFilters,
    fetchAll,
    generateMonthly,
  } = useTenantInvoiceStore();
  const { tenants, fetchAll: fetchTenants } = useAdminTenantStore();

  const [yearFilter, setYearFilter] = useState<number | 'all'>(new Date().getFullYear());
  const [generateOpen, setGenerateOpen] = useState(false);

  useEffect(() => {
    // honor ?tenantId= when navigating from tenant detail
    const tid = searchParams.get('tenantId');
    if (tid) setFilters({ tenantId: tid });
    fetchAll();
    if (tenants.length === 0) fetchTenants();
  }, [fetchAll, fetchTenants, setFilters, searchParams, tenants.length]);

  const tenantNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const tn of tenants) {
      if (tn.id) map[tn.id] = tn.name || tn.id;
    }
    return map;
  }, [tenants]);

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (yearFilter !== 'all' && inv.periodStart) {
        const y = new Date(inv.periodStart).getFullYear();
        if (y !== yearFilter) return false;
      }
      return true;
    });
  }, [invoices, yearFilter]);

  const handleGenerate = async (year: number, month: number) => {
    const result = await generateMonthly(year, month);
    if (result) setGenerateOpen(false);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[22px] sm:text-[24px] font-semibold tracking-tight text-slate-900">
              {t('admin:tenantInvoices.title')}
            </h1>
            <p className="text-[13px] text-slate-500 mt-0.5">
              {t('admin:tenantInvoices.subtitle')}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <Button
              variant="secondary"
              disabled
              title={t('admin:tenantInvoices.tooltips.manualDisabled')}
              leftIcon={<Plus className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto justify-center"
            >
              {t('admin:tenantInvoices.actions.createManual')}
            </Button>
            <Button
              onClick={() => setGenerateOpen(true)}
              leftIcon={<Play className="w-4 h-4" strokeWidth={2} />}
              className="w-full sm:w-auto justify-center"
            >
              {t('admin:tenantInvoices.actions.generateMonthly')}
            </Button>
          </div>
        </div>
      </div>

      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              {t('admin:tenantInvoices.filters.tenant')}
            </label>
            <select
              value={filters.tenantId ?? 'all'}
              onChange={(e) => {
                setFilters({ tenantId: e.target.value === 'all' ? undefined : e.target.value });
                fetchAll();
              }}
              className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value="all">{t('admin:tenantInvoices.filters.all')}</option>
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>
                  {tn.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              {t('admin:tenantInvoices.filters.status')}
            </label>
            <select
              value={filters.status ?? 'all'}
              onChange={(e) => {
                setFilters({
                  status: e.target.value === 'all'
                    ? undefined
                    : (Number(e.target.value) as 0 | 1 | 2 | 3),
                });
                fetchAll();
              }}
              className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value="all">{t('admin:tenantInvoices.filters.all')}</option>
              <option value={TenantInvoiceStatus.Draft}>{t('admin:tenantInvoices.status.draft')}</option>
              <option value={TenantInvoiceStatus.Issued}>{t('admin:tenantInvoices.status.issued')}</option>
              <option value={TenantInvoiceStatus.Paid}>{t('admin:tenantInvoices.status.paid')}</option>
              <option value={TenantInvoiceStatus.Cancelled}>{t('admin:tenantInvoices.status.cancelled')}</option>
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">
              {t('admin:tenantInvoices.filters.year')}
            </label>
            <select
              value={String(yearFilter)}
              onChange={(e) =>
                setYearFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))
              }
              className="w-full px-3 py-1.5 rounded-md bg-white border border-slate-200 text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00A871]/30"
            >
              <option value="all">{t('admin:tenantInvoices.filters.all')}</option>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {error ? (
        <Card>
          <EmptyState
            variant="error"
            title={t('admin:tenantInvoices.errors.fetchFailed')}
            onRetry={fetchAll}
          />
        </Card>
      ) : loading && invoices.length === 0 ? (
        <Card>
          <p className="text-[13px] text-slate-500 text-center py-6">
            {t('common:actions.loading')}
          </p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState title={t('admin:tenantInvoices.empty')} />
        </Card>
      ) : (
        <Card noPadding>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>{t('admin:tenantInvoices.fields.invoiceNumber')}</TableHeader>
                <TableHeader className="hidden sm:table-cell">{t('admin:tenantInvoices.fields.tenant')}</TableHeader>
                <TableHeader>{t('admin:tenantInvoices.fields.period')}</TableHeader>
                <TableHeader className="text-right">{t('admin:tenantInvoices.fields.amount')}</TableHeader>
                <TableHeader>{t('admin:tenantInvoices.fields.status')}</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((inv) => (
                <TableRow
                  key={inv.id}
                  onClick={() => navigate(`/admin/tenant-invoices/${inv.id}`)}
                >
                  <TableCell className="font-mono text-[12px]">
                    {inv.invoiceNumber || t('admin:tenantInvoices.fields.draft')}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    {(inv.tenantId && tenantNames[inv.tenantId]) || '—'}
                  </TableCell>
                  <TableCell>
                    {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {formatMoney(inv.grossAmount, { currency: inv.currency })}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(inv.status)}>
                      {t(`admin:tenantInvoices.status.${statusLabelKey(inv.status)}`)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <GenerateMonthlyModal
        isOpen={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onSubmit={handleGenerate}
        loading={saving}
      />
    </div>
  );
}
